import { useState, useRef, useReducer, useEffect } from 'react'
import { PageFooterWrapper } from '@usb-ui-tools/react-billpay-layout'
import { useSearchService } from '../../../service/payees/search.service'
import { BASE_PATH } from '../../../config/connections'
import {
  payeeDashbord,
  searchedBillerDetails
} from '../../../graphql/cache/localCache'
import SearchDetails from '../../../modules/payees/search/details/search-details'
import SearchHeader from '../../../modules/payees/search/header/search-header'
import routerpath from '../../../constants/routerpath'
import Loader from '../../../components/spinner'
import { routeController } from '../../../router/routeController'
import { appCache } from '../../../context/appCache'
import { PageviewTealiumEvent } from '../../../utils/tealiumwrapper'
import { PAGE_NAMES, BILLER_ACCOUNT_TYPE } from '../../../constants/tealiumData'
import {
  getErrorData,
  errorMessageMappingForForm,
  isGenericError
} from '../../../utils/errorMapping'
import ErrorComponent from '../error/index'
import { configureNativeHeader } from '../../../utils/nativeHandlers'
import {
  getChannelId,
  navigateRoute,
  isOLBOrIpadApp,
  isTuxApp
} from '../../../utils/browserutils'
import { useMultilingualService } from '../../../service/multilingualService'
import MobileBillPayLayout from '../../../modules/layout/MobileBillPayLayout'
import {
  getSessionStorageValue,
  setSessionStorageValue
} from '../../../utils/sessionUtils'
import { updateOnboardingFeatureNew } from '../../../utils/poaUtils'
import { OnboardingSuccessInfoLogger } from '../../../utils/loggerUtils'

/**
 * Add a billl via Search
 *
 * @returns {HTMLElement}
 */
const Search = () => {
  const { loadApi, loading, error, data } = useSearchService()
  const { navigateRoutePath } = navigateRoute()
  const footerRef = useRef()
  let { billerName, billerZipRequired, billerSiteUrl, billerLogoUrl } =
    searchedBillerDetails()

  let fromOnboarding = false
  const onboardingDeeplink = JSON.parse(
    getSessionStorageValue('onboardingBillpayDeeplink')
  )
  if (
    onboardingDeeplink &&
    onboardingDeeplink?.source === 'digitalOnboarding'
  ) {
    fromOnboarding = true
  }
  setSessionStorageValue('fromOnboarding', fromOnboarding)

  if (!billerName && !billerLogoUrl && fromOnboarding) {
    const { marchantDetails } = onboardingDeeplink
    billerName = marchantDetails.billerName
    billerZipRequired = marchantDetails.billerZipRequired
    billerSiteUrl = marchantDetails.billerSiteUrl
    billerLogoUrl = marchantDetails.billerLogoUrl
  }

  const { FOOTER } = useMultilingualService('addBillSearch')
  const [pageErrorMsg, setPageErrorMsg] = useState('')
  const [nickName, setNickName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [zipCode, setZipCode] = useState('')
  const [isFieldsValidationError, setFieldsValidationError] = useState(false)
  const [accountNumberErrorCount, setAccountNumberErrorCount] = useState(0)
  const [zipCodeErrorCount, setZipCodeErrorCount] = useState(0)
  const [isSystemError, setSystemError] = useState(false)
  const [fieldLevelErrorData, setFieldLevelErrorData] = useState({})

  useEffect(() => {
    payeeDashbord({ isDashboard: false })
  }, [payeeDashbord])

  const accountNumberRef = useRef()

  const zipCodeInputRef = useRef()
  const { MESSAGE } = useMultilingualService('addBillSearch')
  const ACTIONS = {
    UPDATE_ACCOUNT_NUMBER: 'update account number',

    UPDATE_ZIP_CODE: 'update zip code'
  }
  const [formValues, dispatch] = useReducer(
    (state, action) => {
      switch (action.type) {
        case ACTIONS.UPDATE_ACCOUNT_NUMBER:
          return { ...state, accountNumber: action.payload }

        case ACTIONS.UPDATE_ZIP_CODE:
          return { ...state, zipCode: action.payload }
        default:
          return ''
      }
    },
    {
      accountNumber: null,

      zipCode: null
    }
  )
  const handleStatusUpdate = (status) => {
    const { inputRef } = status

    switch (inputRef) {
      case accountNumberRef:
        dispatch({ type: ACTIONS.UPDATE_ACCOUNT_NUMBER, payload: status })
        break

      case zipCodeInputRef:
        dispatch({ type: ACTIONS.UPDATE_ZIP_CODE, payload: status })
        break
      default:
        throw new Error(`A input with the name ${inputRef} does not exist`)
    }
  }

  const incrementErrorCounts = (errors) => {
    errors.forEach((err) => {
      switch (err.value) {
        case 'accountNumber':
          setAccountNumberErrorCount(accountNumberErrorCount + 1)
          return
        case 'zipCode':
          setZipCodeErrorCount(zipCodeErrorCount + 1)
          return
        default:
          throw new Error(`No case exists for the value ${error}`)
      }
    })
    if (errors?.length === 0) {
      setZipCodeErrorCount(0)
    }
  }
  // eslint-disable-next-line consistent-return
  const getNickName = () => {
    if (nickName) {
      return nickName
    }
  }

  const getPayload = () => {
    const newZipcode = zipCode?.replace('-', '')
    if (billerZipRequired) {
      return {
        name: billerName,
        nickName: getNickName(),
        sourceURI: billerSiteUrl,
        sourceZipCode: newZipcode,
        payeeAccountInfo: [{ accountNumber }]
        // payeePostalAddress: { postalCode: zipCode }
      }
    }
    return {
      name: billerName,
      nickName: getNickName(),
      sourceURI: billerSiteUrl,
      payeeAccountInfo: [
        {
          accountNumber
        }
      ]
    }
  }

  const onAddpay = () => {
    const errors = []

    Object.keys(formValues).forEach((value) => {
      const formValue = formValues[value]
      const isFieldValidationReq =
        value !== 'zipCode' || (value === 'zipCode' && billerZipRequired)

      if (formValue === null && isFieldValidationReq) {
        const message = errorMessageMappingForForm({
          value,
          pageName: 'addBillSearch'
        })
        errors.push({ value, message })
      }
      if (formValue && formValue.errorCode !== 0 && isFieldValidationReq) {
        const message = errorMessageMappingForForm({
          value,
          pageName: 'addBillSearch',
          input: formValue.inputValue
        })
        errors.push({ value, message })
      }
    })
    incrementErrorCounts(errors)
    if (errors.length > 0) {
      setFieldsValidationError(true)
      if (errors.length === 1) {
        setPageErrorMsg(errors[0].message)
      }
    } else {
      setFieldsValidationError(false)
      loadApi({
        variables: {
          appName: 'BILLPAY',
          payeeCreateRequest: getPayload()
        }
      })
    }
  }

  const cancelBillManually = () => {
    if (fromOnboarding) {
      window.location.href = `${onboardingDeeplink.redirectUrl}/billpay`
    } else {
      navigateRoutePath(`${BASE_PATH}${routerpath.pages.payeedashboard}`)
    }
  }
  const handleFooterClick = (event, type) => {
    if (event && type === 'yes') {
      onAddpay()
    } else if (event && type === 'no') {
      cancelBillManually()
    }
  }

  useEffect(() => {
    document.title = 'Pay bills'
    appCache({ ...appCache(), Title: 'Pay bills' })
    configureNativeHeader(
      'findabilldashboard',
      true,
      BASE_PATH + routerpath.pages.search,
      BASE_PATH + routerpath.pages.payeedashboard
    )
    const reportingData = {
      adddBySearch: true,
      billerType: BILLER_ACCOUNT_TYPE.EXTERNAL,
      accountType: BILLER_ACCOUNT_TYPE.EXTERNAL,
      transactionStatus: 'omni bill pay add biller by search start'
    }
    PageviewTealiumEvent(PAGE_NAMES.ADD_BILL_MANUALLY, reportingData)
  }, [])

  const handleSuccess = () => {
    OnboardingSuccessInfoLogger('DIY_BILLPAY', 'SEARCH_BILL_ENROLLMENT_SUCCESS')
  }
  const handleFailure = () => {
    OnboardingSuccessInfoLogger('DIY_BILLPAY', 'SEARCH_BILL_ENROLLMENT_FAILED')
  }
  useEffect(() => {
    const callPoaAndNavigateToDashboard = async () => {
      if (data?.createBillpayPayee?.payee?.payeeStatus === 'ACTIVE') {
        // call POA here, add bill is success
        if (fromOnboarding) {
          await updateOnboardingFeatureNew(handleSuccess, handleFailure)
        }
        setSessionStorageValue('isBillpaySuccess', 'true')

        const sharedData = {
          currentPath: '/',
          channel: getChannelId(),
          AppFlow: 'OPENBILLPAY',
          AppError: false,
          ReturnURL: '',
          successMsg: isOLBOrIpadApp()
            ? MESSAGE?.BILL_SETUP
            : `${billerName} ${MESSAGE.ADDED}`,
          payeeId: data?.createBillpayPayee?.payee?.payeeId,
          navTo: isOLBOrIpadApp()
            ? routerpath.pages.addedbillerPayment
            : routerpath.pages.reviewdetails,
          fromPayee: fromOnboarding ? null : 'payee_management',
          siderrailLoad: 'ispayeeMangement',
          type: 'add_search',
          data
        }
        routeController(sharedData)
      }
    }
    callPoaAndNavigateToDashboard()
  }, [data])

  useEffect(() => {
    if (error && isGenericError({ error })) {
      setSystemError(true)
    } else if (error) {
      const { message, fieldErrorData } = getErrorData({
        error,
        pageName: 'addEditErrors'
      })

      if (message !== '') {
        setFieldLevelErrorData(fieldErrorData)
        setPageErrorMsg(message)
        setFieldsValidationError(true)
      } else {
        setSystemError(true)
      }
    }
  }, [error])

  if (loading || data) {
    return (
      <div
        data-testId="detailsloading"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100%'
        }}
      >
        <Loader />
      </div>
    )
  }
  return (
    <>
      {!loading && !data && !isSystemError && (
        <PageFooterWrapper
          isOLBOrIpadApp={isOLBOrIpadApp}
          id="add-bill-search-footer"
          ref={footerRef}
          buttonOptions={[
            {
              type: 'primary',
              text: FOOTER?.SEARCH_CONTINUE,
              clickEvent: (e) => {
                handleFooterClick(e, 'yes')
              },
              show: true,
              id: 'edit-bill-save'
            },
            {
              type: 'text',
              text: FOOTER?.SEARCH_CANCEL,
              clickEvent: (e) => {
                handleFooterClick(e, 'no')
              },
              show: true,
              id: 'editbill-cancel-btn'
            }
          ]}
        >
          {!loading && !isSystemError && (
            <>
              {isTuxApp() && <MobileBillPayLayout />}
              <SearchHeader
                {...{
                  isFieldsValidationError,
                  pageErrorMsg
                }}
              />

              <SearchDetails
                {...{
                  accountNumberRef,
                  handleStatusUpdate,
                  setAccountNumber,
                  accountNumber,
                  accountNumberErrorCount,
                  setNickName,
                  billerName,
                  fieldLevelErrorData,
                  zipCodeInputRef,
                  zipCodeErrorCount,
                  setZipCode,
                  billerZipRequired,
                  billerLogoUrl,
                  zipCode,
                  nickName
                }}
              />
            </>
          )}
        </PageFooterWrapper>
      )}
      {isSystemError && <ErrorComponent />}
    </>
  )
}

export default Search
